import { useState } from 'react';
import { SUPERSTITIONS, TABLE_CURIOS, CARVINGS, type ItemSpec } from '../../../engine';
import {
  BARGAINS,
  BENCH_CURIOS,
  FIXTURES,
  ITEM_UNLOCKS,
  SUPERSTITION_INFO,
  type Profile,
} from '../../../campaign';
import { t, type MessageKey } from '../../../i18n';
import { itemName } from '../../../app/describe';
import { formatScore } from '../ScoreBurst';
import { Modal } from '../Modal';
import { Emblem } from './Emblem';
import {
  ItemSheet,
  bargainObject,
  mantelObject,
  showCurio,
  showFixture,
  showOffer,
  type ShowItem,
} from './Item';
import { NightObject, type ObjectSpec } from './Objects';

/**
 * The Ledger (docs/LONG_NIGHT_ART_DIRECTION.md §11.22): a book of dark pages whose entries are the
 * things themselves. Things not yet found are ink silhouettes of their emblems with a faint cold
 * rim and ??? for a name; holding one says, in his ink, how to find it. Pages turn on a Glide.
 * The last section is his collection: every pawn he holds, its record, its name in his ink.
 */

export type LedgerPage =
  'superstitions' | 'curios' | 'appraisals' | 'fixtures' | 'carvings' | 'bargains' | 'his';

const PAGES: readonly LedgerPage[] = [
  'superstitions',
  'curios',
  'appraisals',
  'carvings',
  'bargains',
  'fixtures',
  'his',
];

const APPRAISALS = [
  'ladder',
  'echo',
  'streak',
  'stair',
  'prism',
  'mirror',
  'none',
  'capture',
  'crown',
  'landing',
  'epitaph',
] as const;

interface Entry {
  readonly key: string;
  readonly found: boolean;
  readonly name: string;
  readonly object: ObjectSpec;
  readonly item: () => ShowItem;
  readonly how: string;
}

/** How to find something not yet found, in his words. */
function howToFind(id: string): string {
  const u = ITEM_UNLOCKS[id];
  if (u) return t(`ledgerBook.how.${u.stat}` as MessageKey, { n: u.at });
  return t('ledgerBook.how.counter');
}

function entriesOf(page: LedgerPage, profile: Profile): Entry[] {
  const seen = new Set(profile.ledger?.seen ?? []);
  const item = (id: string): ItemSpec => ({ uid: id, id });
  switch (page) {
    case 'superstitions':
      return SUPERSTITIONS.map((id) => ({
        key: id,
        found: seen.has(id),
        name: itemName(id),
        object: mantelObject(item(id)),
        item: () => showOffer({ kind: 'superstition', id }),
        how: howToFind(id),
      }));
    case 'curios':
      return [...TABLE_CURIOS, ...BENCH_CURIOS].map((id) => ({
        key: id,
        found: seen.has(id),
        name: itemName(id),
        object: { kind: 'curio', emblem: id },
        item: () => showCurio(item(id)),
        how: howToFind(id),
      }));
    case 'appraisals':
      return APPRAISALS.map((id) => ({
        key: id,
        found: seen.has(`appraisal:${id}`),
        name: itemName(`appraisal.${id}`),
        object: { kind: 'appraisal', emblem: `appraisal.${id}` },
        item: () => showOffer({ kind: 'appraisal', id }),
        how: t('ledgerBook.how.counter'),
      }));
    case 'carvings':
      return CARVINGS.map((id) => ({
        key: id,
        found: seen.has(id),
        name: itemName(id),
        object: { kind: 'carving', emblem: `carving.${id}` },
        item: () => showOffer({ kind: 'carving', id }),
        how: t('ledgerBook.how.carving'),
      }));
    case 'bargains':
      return BARGAINS.map((id) => ({
        key: id,
        found: seen.has(id),
        name: itemName(`bargain.${id}`),
        object: bargainObject(id),
        item: () => showOffer({ kind: 'bargain', id }),
        how: t('ledgerBook.how.backRoom'),
      }));
    case 'fixtures':
      return FIXTURES.map((id) => ({
        key: id,
        found: true,
        name: itemName(`fixture.${id}`),
        object: { kind: 'fixture', emblem: `fixture.${id}` },
        item: () => showFixture(id),
        how: '',
      }));
    case 'his':
      return [];
  }
}

export function LedgerBook({
  profile,
  page: initial = 'superstitions',
  onClose,
}: {
  profile: Profile;
  page?: LedgerPage;
  onClose: () => void;
}) {
  const [page, setPage] = useState<LedgerPage>(initial);
  const [turn, setTurn] = useState(0);
  const [open, setOpen] = useState<ShowItem | null>(null);
  const [how, setHow] = useState<Entry | null>(null);
  const entries = entriesOf(page, profile);
  const found = entries.filter((e) => e.found).length;
  const go = (p: LedgerPage) => {
    if (p === page) return;
    setTurn(PAGES.indexOf(p) > PAGES.indexOf(page) ? 1 : -1);
    setPage(p);
  };
  return (
    <Modal
      title={t('ledgerBook.title')}
      onClose={onClose}
      wide
      className="ln-inventory ledger-book"
    >
      <nav className="lb-tabs" aria-label={t('ledgerBook.title')}>
        {PAGES.map((p) => (
          <button
            key={p}
            type="button"
            className={`lb-tab ${p === page ? 'is-on' : ''}`}
            aria-current={p === page}
            onClick={() => go(p)}
          >
            {t(`ledgerBook.page.${p}` as MessageKey)}
          </button>
        ))}
      </nav>
      <div key={page} className={`lb-page ${turn > 0 ? 'turn-next' : turn < 0 ? 'turn-back' : ''}`}>
        {page === 'his' ? (
          <HisCollection profile={profile} />
        ) : (
          <>
            {page !== 'fixtures' && (
              <p className="ln-hint">
                {t('ledgerBook.found', { n: found, total: entries.length })}
              </p>
            )}
            <div className="lb-grid">
              {entries.map((e) => (
                <button
                  key={e.key}
                  type="button"
                  className={`lb-entry ${e.found ? '' : 'is-unfound'} ${
                    page === 'superstitions'
                      ? `rarity-${SUPERSTITION_INFO[e.key as keyof typeof SUPERSTITION_INFO]?.rarity ?? 'common'}`
                      : ''
                  }`}
                  onClick={() => (e.found ? setOpen(e.item()) : setHow(e))}
                >
                  {e.found ? (
                    <NightObject spec={e.object} size={page === 'appraisals' ? 60 : 48} />
                  ) : (
                    <span className="lb-silhouette">
                      <Emblem
                        name={e.object.kind === 'lot' ? 'unknown' : emblemOf(e.object)}
                        size={40}
                        tone="silhouette"
                      />
                    </span>
                  )}
                  <span className="lb-name">{e.found ? e.name : '???'}</span>
                </button>
              ))}
            </div>
          </>
        )}
      </div>
      {open && <ItemSheet item={open} actions={[]} onClose={() => setOpen(null)} />}
      {how && (
        <Modal title="???" onClose={() => setHow(null)} className="ln-sheet">
          <div className="lb-how">
            <Emblem name={emblemOf(how.object)} size={72} tone="silhouette" />
            <p className="lb-how-text">{how.how}</p>
          </div>
        </Modal>
      )}
    </Modal>
  );
}

const emblemOf = (o: ObjectSpec): string => ('emblem' in o ? o.emblem : 'unknown');

/** His collection: every pawn he holds, with its record, its name in his ink. */
function HisCollection({ profile }: { profile: Profile }) {
  const lost = profile.collection;
  if (lost.length === 0) return <p className="lb-empty">{t('ledgerBook.hisEmpty')}</p>;
  return (
    <ul className="lb-his">
      {lost.map((id) => {
        const pawn = profile.pawns[id];
        const r = pawn?.records;
        return (
          <li key={id}>
            <Emblem name="the-unredeemed" size={40} />
            <div>
              <p className="lb-his-name">{pawn?.name ?? id}</p>
              {r && (
                <p className="lb-his-record">
                  {t('ledgerBook.record', {
                    tables: r.tablesPlayed,
                    captures: r.captures,
                    best: formatScore(r.bestChain),
                  })}
                </p>
              )}
            </div>
          </li>
        );
      })}
    </ul>
  );
}
